import { Card, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { usePackageCatalog, type CatalogService } from '../../hooks/usePackageCatalog'
import { formatServicePrice } from '../../utils/formatServicePrice'

interface ReviewServicesCardProps {
  selectedIds: number[]
  /** Tipo de facturación (opcional). Por defecto mensual; define el precio sumado. */
  billingType?: 'MONTHLY' | 'ANNUAL'
  /** Si se provee, muestra un enlace "Editar" que vuelve al paso de servicios. */
  onEdit?: () => void
}

/**
 * Card compartida por los ReviewStep de los wizards: lista los servicios
 * elegidos con su precio y un único subtotal (calculado por defecto), más los
 * avisos de negocio inactivo y subtotal estimado.
 */
export function ReviewServicesCard({ selectedIds, billingType = 'MONTHLY', onEdit }: ReviewServicesCardProps) {
  const { byId } = usePackageCatalog()
  const annual = billingType === 'ANNUAL'

  const selected = selectedIds
    .map((id) => byId.get(id))
    .filter((s): s is CatalogService => Boolean(s))

  const unitOf = (s: CatalogService) => (annual ? s.annualPrice : s.monthlyPrice)
  const total = selected.reduce((sum, s) => sum + unitOf(s), 0)
  const currencyName = selected[0]?.currencyName ?? ''

  return (
    <Card>
      <CardHeader>
        <CardTitle>Servicios</CardTitle>
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="text-xs font-semibold text-brand hover:underline"
          >
            Editar
          </button>
        )}
      </CardHeader>

      {selected.length === 0 ? (
        <p className="text-sm text-ink-muted">No se seleccionaron servicios.</p>
      ) : (
        <div className="grid gap-2">
          {selected.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-4 text-sm">
              <span className="text-ink-muted">{s.name}</span>
              <span className="text-right font-medium text-ink">
                {s.hasPrice ? formatServicePrice(unitOf(s), s.currencyName) : 'A confirmar'}
              </span>
            </div>
          ))}

          <div className="mt-2 flex items-center justify-between gap-4 border-t border-line pt-3">
            <span className="text-sm font-semibold text-ink">Subtotal</span>
            <span className="text-base font-bold text-brand">
              {formatServicePrice(total, currencyName)}
            </span>
          </div>
        </div>
      )}

      <p className="mt-4 rounded-lg bg-warning-soft p-3 text-xs text-[#9a3412]">
        El negocio se creará <strong>inactivo</strong> y se activará cuando se complete el
        pago de la primera factura.
      </p>
      <p className="mt-2 text-[11px] text-ink-subtle">
        Estimado. El monto final se confirma en tu primera factura (puede prorratearse según
        la fecha de creación).
      </p>
    </Card>
  )
}
